// useTaskGridUpdates.ts
import React, { RefObject, useMemo, useState } from 'react'
import { DynamicMassUpdateData, Subtask, Task } from '../../types'
import { useUpdateTask } from '../../api/aws/useUpdateTask'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useTheme } from '@mui/material'
import { GridApiPremium, GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { useBatchDeleteTasks } from '../../api/aws/tasks/useBatchDeleteTasks'
import { useEntitiesFromSearch } from 'api/search'

interface UseTaskGridUpdatesArgs {
  tasks: Task[]
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>
  setIsMassDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  apiRef: RefObject<GridApiPremium>
}

const isSubtaskSK = (sk?: string) => !!sk && sk.startsWith('SUBTASK_')

type PKSK = { PK: string; SK: string }

export const useTaskGridUpdates = ({
  tasks,
  setTasks,
  setIsMassDeleteOpen,
  setAnchorEl,
  apiRef,
}: UseTaskGridUpdatesArgs) => {
  const _theme = useTheme()
  const [massUpdateData, setMassUpdateData] = useState<DynamicMassUpdateData>({})
  // ensure allTasks is typed, not unknown[]
  const { allTasks = [] as Task[] } = useEntitiesFromSearch()
  const { mutate: updateTask } = useUpdateTask()
  const { mutate: batchDeleteTasks } = useBatchDeleteTasks()
  const { showSnackbar } = useSnackbarContext()
  // ---- 1) Build a fast lookup for subtasks by parent SK --------------------
  const subtasksByParentSK = useMemo(() => {
    const m = new Map<string, PKSK[]>()

    // a) nested
    for (const t of allTasks) {
      const nested = (t as any).subtasks as Subtask[] | undefined
      if (Array.isArray(nested) && nested.length) {
        const arr = m.get(t.SK) ?? []
        for (const s of nested) arr.push({ PK: s.PK ?? t.PK, SK: s.SK })
        m.set(t.SK, arr)
      }
    }

    // b) flattened (parentSK/parentTaskSK/parentId)
    for (const t of allTasks) {
      const parent: string | undefined = (t as any).parentSK || (t as any).parentTaskSK || (t as any).parentId
      if (isSubtaskSK(t.SK) && parent) {
        const arr = m.get(parent) ?? []
        arr.push({ PK: t.PK, SK: t.SK })
        m.set(parent, arr)
      }
    }

    // de-dupe each array (no for..of over Map to avoid downlevelIteration)
    m.forEach((arr, key) => {
      const map = new Map<string, PKSK>(arr.map(x => [`${x.PK}::${x.SK}`, x]))
      m.set(key, Array.from(map.values()))
    })
    return m
  }, [allTasks])

  // Utility: cascade delete payload for a *parent* task
  const cascadeForParent = (PK: string, SK: string) => {
    const base = [{ tableName: 'TASKS_TABLE' as const, PK, SK }]
    const subs: PKSK[] = subtasksByParentSK.get(SK) ?? []
    return base.concat(subs.map(s => ({ tableName: 'TASKS_TABLE' as const, PK: s.PK, SK: s.SK })))
  }

  // Utility: de-dupe any delete payload (strongly typed)
  const dedupePayload = <T extends PKSK>(items: T[]): T[] => {
    const map = new Map<string, T>(items.map(x => [`${x.PK}::${x.SK}`, x]))
    return Array.from(map.values())
  }

  const handleMassUpdateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setMassUpdateData(prev => ({ ...prev, [name]: value }))
  }

  const handleTaskUpdate = async (taskIdentifiers: Task[], updatedData: Partial<Task>) => {
    if (!taskIdentifiers.length) return
    const validTasks = taskIdentifiers.filter(task => task.PK && task.SK)
    if (!validTasks.length) return
    const { PK, SK } = validTasks[0]
    // @ts-ignore shape matches your mutation
    updateTask(
      //@ts-ignore
      { taskPK: PK, taskSK: SK, ...updatedData },
      {
        onSuccess: () => {
          setTasks(prev => prev.map(task => (task.PK === PK && task.SK === SK ? { ...task, ...updatedData } : task)))
        },
        onError: err => console.error('[handleTaskUpdate] failed', err),
      }
    )
  }

  const handleSendForReview = (task: Task) => {
    // @ts-ignore
    handleTaskUpdate([task], { mode: 'review', masterStatus: 'Needs Review', queue: task?.assigner })
    showSnackbar('Task sent for review!', 'success')
  }

  const handleMassDelete = async (
    setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>,
    rowSelectionModel: GridRowSelectionModel,
    setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>,
    setSelectedMassData: React.Dispatch<React.SetStateAction<Task[] | []>>,
    setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsDeleting(true)
    const ids = Array.from(rowSelectionModel?.ids ?? [])
    if (!ids.length) return

    let payload: { tableName: 'TASKS_TABLE'; PK: string; SK: string }[] = []

    for (const id of ids) {
      const [PK, SK] = String(id).split('::')
      if (isSubtaskSK(SK)) {
        payload.push({ tableName: 'TASKS_TABLE', PK, SK })
      } else {
        payload = payload.concat(cascadeForParent(PK, SK))
      }
    }
    payload = dedupePayload(payload)

    batchDeleteTasks(payload, {
      onSuccess: () => {
        showSnackbar(`${payload.length} item(s) deleted.`, 'success')
        setRowSelectionModel({ ids: new Set<string>(), type: 'include' })
        setSelectedTask(null)
        setSelectedMassData([])
        setIsMassDeleteOpen(false)
      },
      onError: error => {
        showSnackbar(error.message || 'Failed to delete selected items.', 'error')
        setIsMassDeleteOpen(false)
      },
      onSettled: () => {
        setIsDeleting(false)
      },
    })
  }

  const handleConfirmDelete = async (
    selectedTask: Task | null,
    setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>,
    setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsDeleting(true)
    if (!selectedTask) return void setIsDeleting(false)

    let payload: { tableName: 'TASKS_TABLE'; PK: string; SK: string }[]
    if (isSubtaskSK(selectedTask.SK)) {
      payload = [{ tableName: 'TASKS_TABLE', PK: selectedTask.PK, SK: selectedTask.SK }]
    } else {
      payload = cascadeForParent(selectedTask.PK, selectedTask.SK)
    }
    payload = dedupePayload(payload)

    batchDeleteTasks(payload, {
      onSuccess: () => {
        setSelectedTask(null)
        showSnackbar(
          `Deleted ${isSubtaskSK(selectedTask.SK) ? 'subtask' : 'task'}${
            payload.length > 1 ? ` (+${payload.length - 1} subtasks)` : ''
          } successfully.`,
          'success'
        )
        setIsDeleting(false)
      },
      onError: error => {
        showSnackbar(error.message || 'Failed to delete.', 'error')
        setIsDeleting(false)
      },
    })
  }

  const handleDeleteClick = (rowSelectionModel: GridRowSelectionModel) => {
    const selectedRowId = Array.from(rowSelectionModel?.ids)?.[0]
    if (!selectedRowId) return
    const rowElement = apiRef.current.getRowElement(selectedRowId)
    if (rowElement) {
      rowElement.classList.add('highlighted-row')
      setAnchorEl(rowElement)
    }
  }

  const handleClickMassDelete = (
    rowSelectionModel: GridRowSelectionModel,
    setSelectedMassData: React.Dispatch<React.SetStateAction<Task[]>>
  ) => {
    if (!Array.from(rowSelectionModel?.ids)?.length) return
    const selected: Task[] = []
    const ids = Array.from(rowSelectionModel?.ids ?? [])
    for (const id of ids) {
      const [_, SK] = String(id).split('::')
      const matched = tasks.find(t => t.SK === SK) || allTasks.find(t => t.SK === SK)
      if (matched) selected.push(matched)
    }
    setSelectedMassData(selected)
    setIsMassDeleteOpen(true)
  }

  return {
    handleSendForReview,
    handleMassUpdateChange,
    handleMassDelete,
    handleDeleteClick,
    handleClickMassDelete,
    handleConfirmDelete,
    massUpdateData,
    setMassUpdateData,
  }
}

// import React, { MutableRefObject, RefObject, useState } from 'react'
// import { DynamicMassUpdateData, Subtask, Task } from '../../types'
// import { useUpdateTask } from '../../api/aws/useUpdateTask'
// import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
// import { useTheme } from '@mui/material'
// import { GridApiPremium, GridRowSelectionModel } from '@mui/x-data-grid-premium'
// import { useBatchDeleteTasks } from '../../api/aws/tasks/useBatchDeleteTasks'
// import { useEntitiesFromSearch } from 'api/search'

// interface UseTaskGridUpdatesArgs {
//   tasks: Task[]
//   setTasks: React.Dispatch<React.SetStateAction<Task[]>>
//   setIsMassDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>
//   setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>
//   apiRef: RefObject<GridApiPremium>
//   // selectedTask: Task | null
//   // setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>
//   // massUpdateData: DynamicMassUpdateData
//   // setMassUpdateData: React.Dispatch<React.SetStateAction<DynamicMassUpdateData>>
// }

// export const useTaskGridUpdates = ({
//   tasks,
//   setTasks,
//   setIsMassDeleteOpen,
//   setAnchorEl,
//   apiRef,
// }: // selectedTask,
// // setSelectedTask,
// // massUpdateData,
// // setMassUpdateData,
// UseTaskGridUpdatesArgs) => {
//   const theme = useTheme()
//   const [massUpdateData, setMassUpdateData] = useState<DynamicMassUpdateData>({})
//   const { allTasks } = useEntitiesFromSearch()
//   const { mutate: updateTask } = useUpdateTask()
//   const { mutate: batchDeleteTasks } = useBatchDeleteTasks()
//   const { showSnackbar } = useSnackbarContext()

//   const handleMassUpdateChange = e => {
//     const { name, value } = e.target
//     setMassUpdateData(prev => ({ ...prev, [name]: value }))
//   }

//   const handleTaskUpdate = async (taskIdentifiers: Task[], updatedData: Partial<Task>) => {
//     if (!taskIdentifiers.length) return
//     // Ensure all task identifiers have required fields
//     const validTasks = taskIdentifiers.filter(task => task.PK && task.SK)
//     if (!validTasks.length) {
//       console.warn('[handleTaskUpdate] No valid tasks to update.')
//       return
//     }
//     const { PK, SK } = validTasks[0]
//     const updateData = { taskPK: PK, taskSK: SK, ...updatedData }
//     console.log('Data used for update (single update): ', updateData)

//     updateTask(
//       //@ts-ignore
//       { taskPK: PK, taskSK: SK, ...updatedData },
//       {
//         onSuccess: () => {
//           setTasks(prev => prev.map(task => (task.PK === PK && task.SK === SK ? { ...task, ...updatedData } : task)))
//         },
//         onError: error => console.error('[handleTaskUpdate] Single update failed', error),
//       }
//     )
//   }
//   const handleSendForReview = (task: Task) => {
//     const updatePayload = {
//       mode: 'review', // or 'needs_review' if you want it clearer
//       masterStatus: 'Needs Review',
//       queue: task?.assigner,
//     }
//     //@ts-ignore
//     handleTaskUpdate([task], updatePayload)
//     showSnackbar('Task sent for review!', 'success')
//   }

//   const handleMassDelete = async (
//     setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>,
//     rowSelectionModel: GridRowSelectionModel,
//     setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>,
//     setSelectedMassData: React.Dispatch<React.SetStateAction<Task[] | []>>
//   ) => {
//     // Support deleting both tasks and subtasks
//     const deletePayload = Array.from(rowSelectionModel?.ids).map(rowId => {
//       const [PK, SK] = (rowId as string).split('::')
//       return {
//         tableName: 'TASKS_TABLE',
//         PK,
//         SK,
//       }
//     })

//     batchDeleteTasks(deletePayload, {
//       onSuccess: () => {
//         showSnackbar(`${deletePayload.length} item(s) deleted.`, 'success')
//         setRowSelectionModel({
//           ids: new Set<string>(),
//           type: 'include',
//         })
//         setSelectedTask(null)
//         setSelectedMassData([])
//         setIsMassDeleteOpen(false)
//       },
//       onError: error => {
//         showSnackbar(error.message || 'Failed to delete selected items.', 'error')
//         setIsMassDeleteOpen(false)
//       },
//     })
//   }
//   const handleDeleteClick = (
//     // setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>,
//     rowSelectionModel: GridRowSelectionModel
//   ) => {
//     const selectedRowId = Array.from(rowSelectionModel?.ids)?.[0]
//     if (!selectedRowId) return

//     const [PK, SK] = (selectedRowId as string).split('::')

//     let newSelectedTask = tasks.find(task => task.SK === SK)
//     let newSelectedSubtasks = allTasks?.find
//     // 🔍 If it's a subtask, find the parent then find the subtask inside it
//     if (!newSelectedTask && SK.startsWith('SUBTASK_')) {
//       for (const task of tasks) {
//         const subtask = task.subtasks?.find((sub: Subtask) => sub.SK === SK)
//         if (subtask) {
//           newSelectedTask = {
//             ...subtask,
//             parentSK: task.SK,
//             assignees: task.assignees ?? [],
//           }
//           break
//         }
//       }
//     }

//     // setSelectedTask(newSelectedTask || null)

//     const rowElement = apiRef.current.getRowElement(selectedRowId)
//     if (rowElement) {
//       rowElement.classList.add('highlighted-row')
//       setAnchorEl(rowElement)
//     }
//   }
//   const handleClickMassDelete = (
//     rowSelectionModel: GridRowSelectionModel,
//     setSelectedMassData: React.Dispatch<React.SetStateAction<Task[]>>
//   ) => {
//     if (!Array.from(rowSelectionModel?.ids)?.length) return

//     const selectedTasksOrSubtasks = []

//     Array.from(rowSelectionModel?.ids).forEach(id => {
//       const [PK, SK] = (id as string).split('::')

//       // First, try to find a parent task match
//       let matchedTask = tasks.find(task => task.SK === SK)

//       // If not a parent, try to find a subtask
//       if (!matchedTask && SK.startsWith('SUBTASK_')) {
//         for (const task of tasks) {
//           const subtask = task.subtasks?.find((sub: Subtask) => sub.SK === SK)
//           if (subtask) {
//             matchedTask = {
//               ...subtask,
//               parentSK: task.SK,
//               assignees: task.assignees ?? [],
//             }
//             break
//           }
//         }
//       }

//       if (matchedTask) {
//         selectedTasksOrSubtasks.push(matchedTask)
//       }
//     })
//     setSelectedMassData(selectedTasksOrSubtasks)
//     setIsMassDeleteOpen(true)
//     console.log('🧾 Selected for deletion:', selectedTasksOrSubtasks)
//   }

//   const handleConfirmDelete = async (
//     selectedTask: Task | null,
//     setSelectedTask: React.Dispatch<React.SetStateAction<Task | null>>,
//     setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>
//   ) => {
//     setIsDeleting(true)

//     if (selectedTask) {
//       const deletePayload = [
//         {
//           tableName: 'TASKS_TABLE',
//           PK: selectedTask.PK,
//           SK: selectedTask.SK,
//         },
//       ]

//       // If it's a task (not subtask) and has subtasks, include them
//       if (!selectedTask.SK.startsWith('SUBTASK_') && selectedTask.subtasks?.length > 0) {
//         selectedTask.subtasks.forEach((sub: Subtask) => {
//           deletePayload.push({
//             tableName: 'TASKS_TABLE',
//             PK: selectedTask.PK,
//             SK: sub.SK,
//           })
//         })
//       }

//       batchDeleteTasks(deletePayload, {
//         onSuccess: () => {
//           setSelectedTask(null)
//           showSnackbar(
//             `Deleted ${selectedTask.SK.startsWith('SUBTASK_') ? 'subtask' : 'task'} successfully.`,
//             'success'
//           )
//           setIsDeleting(false)
//         },
//         onError: error => {
//           showSnackbar(error.message || 'Failed to delete.', 'error')
//           setIsDeleting(false)
//         },
//       })
//     }
//   }

//   return {
//     handleSendForReview,
//     handleMassUpdateChange,
//     handleMassDelete,
//     handleDeleteClick,
//     handleClickMassDelete,
//     handleConfirmDelete,
//     massUpdateData,
//     setMassUpdateData,
//   }
// }
